import React from "react";
import { formatNumber, formatDate } from "@/lib/utils";
import { numberToVietnameseWords } from "@/lib/numberToWords";
import { getCompanyConfig } from "@/config/company";
import ISOPrintHeader from "@/components/shared/print/ISOPrintHeader";
import { PrintDocumentTitle, PrintInfoGrid } from "@/components/shared/print";

interface VendorDebtReconciliationPrintTemplateProps {
  data?: any;
  config?: any;
}

export default function VendorDebtReconciliationPrintTemplate({
  data = {},
  config,
}: VendorDebtReconciliationPrintTemplateProps) {
  const c = getCompanyConfig(config);
  const docId =
    data?.code ||
    data?.vendorId ||
    (data?.id?.length > 20 ? data.id.slice(0, 8).toUpperCase() : data?.id) ||
    "AP";

  const closingDebt = Number(data.closingBalance || data.currentDebt || 0);

  return (
    <div
      className="p-[10mm] text-black leading-snug text-[12px] bg-white w-full mx-auto min-h-[297mm]"
      style={{ fontFamily: "'Times New Roman', Times, serif" }}
      id="print-area"
    >
      {/* ========== COMPANY HEADER ========== */}
      <ISOPrintHeader
        config={config}
      />

      {/* ========== TITLE (CENTERED, NO QR) ========== */}
      <PrintDocumentTitle
        title="BIÊN BẢN ĐỐI SOÁT CÔNG NỢ NCC"
        subtitle={`VENDOR DEBT RECONCILIATION • Đến ngày ${formatDate(data.asOfDate || new Date())}`}
        docNo={docId}
        date={data.asOfDate || new Date()}
        dateLabel="Ngày đối soát"
      />

      {/* ========== PARTIES INFO (BORDERLESS 2-COL TABS) ========== */}
      <div className="mb-4">
        <PrintInfoGrid
          labelWidth="w-[125px]"
          leftColumn={[
            {
              label: "Bên Mua (Bên A)",
              value: c.companyName,
              isBold: true,
            },
            {
              label: "Địa chỉ",
              value: c.address1 || c.address2 || "—",
            },
            {
              label: "Mã số thuế",
              value: c.taxCode || "—",
            },
            {
              label: "Đại diện",
              value: "Ban Kế Toán Tài Chính APV",
            },
          ]}
          rightColumn={[
            {
              label: "Bên Bán / NCC",
              value: data.vendorName || data.name || "—",
              isBold: true,
            },
            {
              label: "Mã NCC",
              value: data.vendorCode || data.code || "—",
              isBold: true,
              highlight: true,
            },
            {
              label: "Địa chỉ NCC",
              value: data.vendorAddress || data.address || "—",
            },
            {
              label: "Người liên hệ",
              value: `${data.contactName || data.contactPerson || "Đại diện Kế toán công nợ"}${data.phone ? ` (${data.phone})` : ""}`,
            },
          ]}
        />
      </div>

      {/* ========== 4-BOX BALANCE SUMMARY ========== */}
      <div className="grid grid-cols-4 gap-2 mb-4 text-center">
        <div className="border border-black p-2 bg-slate-50">
          <div className="text-[10px] text-slate-700 font-semibold">NỢ ĐẦU KỲ (A)</div>
          <div className="text-xs font-bold text-slate-900 mt-1 font-mono">
            {formatNumber(data.openingBalance || 0)} ₫
          </div>
        </div>
        <div className="border border-black p-2 bg-blue-50/60">
          <div className="text-[10px] text-blue-900 font-semibold">MUA TRONG KỲ (B)</div>
          <div className="text-xs font-bold text-blue-900 mt-1 font-mono">
            +{formatNumber(data.totalIncurred || data.purchaseAmount || 0)} ₫
          </div>
        </div>
        <div className="border border-black p-2 bg-emerald-50/60">
          <div className="text-[10px] text-emerald-900 font-semibold">ĐÃ THANH TOÁN (C)</div>
          <div className="text-xs font-bold text-emerald-900 mt-1 font-mono">
            -{formatNumber(data.totalPaid || data.paidAmount || 0)} ₫
          </div>
        </div>
        <div className="border-2 border-black p-2 bg-yellow-100/70">
          <div className="text-[10px] text-red-900 font-bold">CÒN PHẢI TRẢ (A + B - C)</div>
          <div className="text-sm font-extrabold text-red-700 mt-1 font-mono">
            {formatNumber(closingDebt)} ₫
          </div>
        </div>
      </div>

      {/* ========== CLAUSE & WORDS ========== */}
      <div className="border border-black p-3 mb-6 text-[11px] leading-relaxed bg-slate-50/40 rounded-sm">
        <p className="mb-1.5">
          Hai bên đã cùng nhau kiểm tra, đối chiếu toàn bộ các hóa đơn, phiếu nhập kho mua hàng và chứng từ thanh toán ngân hàng phát sinh tính đến ngày <b>{formatDate(data.asOfDate || new Date())}</b>.
        </p>
        <p className="mb-1.5">
          Hai bên thống nhất xác nhận số tiền Bên A (APV Packaging) còn phải thanh toán cho Bên B (Nhà cung cấp) là:{" "}
          <span className="font-bold text-red-700 font-mono text-[13px]">
            {formatNumber(closingDebt)} VNĐ
          </span>.
        </p>
        <p className="italic">
          <b>Số tiền viết bằng chữ:</b>{" "}
          {closingDebt > 0
            ? numberToVietnameseWords(closingDebt, "đồng")
            : "Không đồng"}
        </p>
      </div>

      {/* ========== 2-PARTY SIGNATURES ========== */}
      <div className="flex justify-between mt-12 text-[11px] break-inside-avoid">
        <div className="text-center w-[45%]">
          <div className="font-bold uppercase mb-1">ĐẠI DIỆN BÊN MUA (APV PACKAGING)</div>
          <div className="text-slate-500 italic text-[10px] mb-16">(Ký tên, đóng dấu xác nhận)</div>
          <div className="font-medium text-slate-800">Ban Tài Chính - Kế Toán APV</div>
        </div>
        <div className="text-center w-[45%]">
          <div className="font-bold uppercase mb-1">ĐẠI DIỆN BÊN BÁN (NHÀ CUNG CẤP)</div>
          <div className="text-slate-500 italic text-[10px] mb-16">(Xác nhận số dư, ký tên &amp; đóng dấu)</div>
          <div className="font-medium text-slate-800">{data.vendorName || data.name || "Đại diện NCC"}</div>
        </div>
      </div>
    </div>
  );
}
